import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Brush
} from 'recharts';
import { format } from 'date-fns';
import { GroupPerformanceData } from '@/types/performance-analyze';

interface PerformanceChartProps {
  data: any[];
  groups: GroupPerformanceData[];
}

const COLORS = [
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7c7c',
  '#a4de6c',
  '#d084d0',
  '#8dd1e1',
  '#ffa07a',
  '#98d8c8',
  '#f6b93b',
  '#e55039',
  '#4a69bd'
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload) return null;
  
  return (
    <div className="bg-card border rounded-lg p-3 shadow-lg">
      <p className="font-semibold mb-2">{format(new Date(label), 'MMM dd, yyyy')}</p>
      {payload.map((entry: any, index: number) => {
        const groupId = entry.dataKey.split('_')[1];
        const total = entry.payload[`total_${groupId}`];
        const winning = entry.payload[`winning_${groupId}`];
        
        return (
          <div key={index} className="text-sm mb-1">
            <span style={{ color: entry.color }}>●</span>
            <span className="ml-2">{entry.name}:</span>
            <span className="ml-2 font-semibold">{entry.value?.toFixed(2)}%</span>
            <span className="ml-2 text-muted-foreground">
              ({winning}/{total} signals)
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const PerformanceChart = ({ data, groups }: PerformanceChartProps) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-[500px] flex items-center justify-center border rounded-lg bg-muted/10">
        <p className="text-muted-foreground">No data to display. Select groups and apply filters.</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={500}>
        <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="date" 
            tickFormatter={(date) => format(new Date(date), 'MM/dd')}
            className="text-xs"
          />
          <YAxis 
            label={{ 
              value: 'Winrate (%)', 
              angle: -90, 
              position: 'insideLeft',
              className: 'text-sm'
            }}
            domain={[0, 100]}
            className="text-xs"
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            formatter={(value, entry: any) => {
              const group = groups.find(g => `winrate_${g.group_id}` === entry.dataKey);
              return group ? group.group_name : value;
            }}
          />
          <Brush dataKey="date" height={30} stroke="hsl(var(--primary))" />
          
          {groups.map((group, index) => (
            <Line
              key={group.group_id}
              type="monotone"
              dataKey={`winrate_${group.group_id}`}
              stroke={COLORS[index % COLORS.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
              name={group.group_name}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
